import React, { useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import Close from 'core/icons/Close';

import {
	Background,
	Cta,
	CloseButton,
	Container,
} from './mpBannerStyles';
import { Transition } from 'react-transition-group';
import useToggle from 'core/hooks/useToggle';
import { trackEvent } from 'core/utils';
import Markdown from 'markdown-to-jsx';

const Banner = ({
	id,
	message,
	settings,
	delay,
	withCookie,
	dismissable,
	target,
}) => {
	const {
		cta,
		color,
		backgroundColor,
	} = settings;

	const containerEl = useRef();

	const [open, toggle] = useToggle(true);

	const getTrackingParams = useCallback(() => ({
		statjoy: [{
			cus1: id,
			cus2: target,
		}],
		fullstory: [{
			id_int: id,
			target_str: target
		}],
	}), [id, target]);

	const onDismiss = () => {
		if (! dismissable) return;
		toggle();
		if (dismissable) trackEvent('MpBannerDismiss', getTrackingParams());
		if (! withCookie) return;
		const COOKIE_NAME = `mpb`;
		const existing = Cookies.get(COOKIE_NAME);
		// If we have a cookie - append the current id...
		Cookies.set(COOKIE_NAME, existing ? `${existing}-${id}` : `${id}`, { expires: 1 });
	};

	const onClick = () => {
		if (! dismissable) return;
		trackEvent('MpBannerHit', getTrackingParams());
	};

	useEffect(() => {
		if (dismissable) trackEvent('MpBannerView', getTrackingParams());
	}, [dismissable, getTrackingParams]);

	const containerProps = {
		ref: containerEl,
		textColor: color,
		backgroundColor,
		delay,
	};

	return (
		<Transition in={open} timeout={delay} unmountOnExit>{status => (
			<Background {...containerProps} status={status}>
				<Container>
					<Cta as={cta ? "a" : "p"} href={cta} title={message} onClick={onClick}>
						<Markdown>{message}</Markdown>
					</Cta>

					<CloseButton onClick={onDismiss} aria-label="Dismiss banner">
						<Close fill="currentColor" width={12} height={12} />
						<span>Close</span>
					</CloseButton>
				</Container>
			</Background>
		)}</Transition>
	);
};

Banner.propTypes = {
	id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	message: PropTypes.string.isRequired,
	target: PropTypes.string.isRequired,
	settings: PropTypes.shape({
		cta: PropTypes.string,
		color: PropTypes.string,
		backgroundColor: PropTypes.string,
	}),
	delay: PropTypes.number,
	withCookie: PropTypes.bool,
	dismissable: PropTypes.bool,
};

Banner.defaultProps = {
	delay: 250,
	withCookie: true,
	dismissable: true,
};

export default Banner;
